import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Braces } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await login(username.trim(), password);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 grid-lines pointer-events-none" />
        <div
          className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full opacity-30 blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, hsl(45 93% 47% / 0.4), transparent 60%)" }}
        />
        <Link to="/" className="flex items-center gap-2 relative">
          <div className="w-8 h-8 rounded-sm bg-accent text-accent-foreground grid place-items-center">
            <Braces className="w-4 h-4" strokeWidth={2} />
          </div>
          <span className="font-display font-bold text-xl">MLForge</span>
        </Link>
        <div className="relative">
          <div className="label-mono mb-3">// AI ML ENGINEER</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter leading-[1.05] max-w-md">
            Upload a dataset.<br />Ship a model.
          </h1>
          <p className="text-sm text-muted-foreground mt-4 max-w-md leading-relaxed">
            Sign in to your workspace. Your projects, datasets and trained models stay yours.
          </p>
        </div>
        <div className="label-mono relative">v1.1 · JWT-secured</div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div>
            <div className="label-mono mb-2">// SIGN IN</div>
            <h2 className="font-display font-bold text-3xl tracking-tighter">Welcome back</h2>
            <p className="text-sm text-muted-foreground mt-1">
              No account? <Link to="/register" className="text-accent hover:underline" data-testid="link-register">Create one</Link>.
            </p>
          </div>
          <div>
            <Label className="label-mono">Username</Label>
            <Input
              data-testid="login-username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              className="mt-2 bg-background"
            />
          </div>
          <div>
            <Label className="label-mono">Password</Label>
            <Input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="mt-2 bg-background"
            />
          </div>
          {error && (
            <div className="text-xs text-destructive border border-destructive/30 bg-destructive/5 rounded-sm px-3 py-2" data-testid="login-error">
              {error}
            </div>
          )}
          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm h-11"
          >
            {busy ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>
        </form>
      </div>
    </div>
  );
}
